import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Radio, CalendarDays, Users, ArrowRight, Check, Loader2 } from "lucide-react";
import { LivePassSignupCard } from "@/components/LivePassSignupCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIAS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MESES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fecha = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return { dia: DIAS[dt.getDay()], num: d, mes: MESES[m - 1] };
};

const hora = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function LivePublic() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/live/sessions`, { params: { limit: 8 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ sessions: [], price: 29.99, trial_days: 7 }));
  }, []);

  const dias = data?.trial_days ?? 7;
  // Quien llega desde el formulario incrustado vuelve acá a escribir su código.
  const query = new URLSearchParams(window.location.search);
  const paso = { code: query.get("step") === "code", email: query.get("email") || "" };
  const precio = data?.price ?? 29.99;

  return (
    <div className="min-h-screen bg-[#0d1b2a] text-white">
      {/* Portada */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px)",
                      backgroundSize: "28px 28px" }} />
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-12">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400">
            <Radio className="h-3.5 w-3.5" /> We Speak Spanish
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 leading-[1.05]">
            WSS Live
          </h1>
          <p className="text-base md:text-lg text-slate-300 mt-4 max-w-2xl leading-relaxed">
            Live conversation classes with Costa Rican teachers, several times a week, grouped by
            level. Book the one that fits your schedule and join from anywhere.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <a href="#registro">
              <Button size="lg" className="bg-amber-400 text-[#0d1b2a] hover:bg-amber-300 rounded-full font-semibold"
                      data-testid="live-public-cta-top">
                Try {dias} days free
              </Button>
            </a>
            <a href="#calendario" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5"
               data-testid="live-public-see-calendar">
              See the next classes <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <p className="text-xs text-slate-400 mt-4">
            After the trial, ${Number(precio).toFixed(2)} a month. Cancel whenever you want.
          </p>
        </div>
      </div>

      {/* Calendario real */}
      <div id="calendario" className="bg-white text-slate-900">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <CalendarDays className="h-3.5 w-3.5" /> Upcoming classes
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-2">
            Here is what is coming up
          </h2>
          <p className="text-sm text-slate-500 mt-1">Costa Rica time. Updated automatically.</p>

          {!data ? (
            <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
          ) : data.sessions?.length ? (
            <div className="mt-7 grid gap-3" data-testid="live-public-sessions">
              {data.sessions.map((s, i) => {
                const f = fecha(s.date);
                return (
                  <div key={i} className="group flex items-center gap-4 border border-slate-200 rounded-xl p-4 hover:border-amber-400 hover:shadow-sm transition-all">
                    <div className="w-14 shrink-0 text-center">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">{f.mes}</div>
                      <div className="font-display text-2xl font-bold leading-none">{f.num}</div>
                      <div className="text-[10px] text-slate-400 capitalize">{f.dia.slice(0, 3)}</div>
                    </div>
                    <div className="h-10 w-px bg-slate-100" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[15px] truncate">{s.title}</div>
                      <div className="text-[12.5px] text-slate-500">
                        {hora(s.start_time)}{s.end_time ? ` – ${hora(s.end_time)}` : ""}
                        {s.activity_type ? ` · ${s.activity_type}` : ""}
                      </div>
                    </div>
                    {(s.levels || []).length > 0 && (
                      <div className="hidden sm:flex gap-1">
                        {s.levels.map((l) => (
                          <span key={l} className="text-[10px] font-bold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">{l}</span>
                        ))}
                      </div>
                    )}
                    {s.capacity > 0 && (
                      <span className="hidden md:inline-flex items-center gap-1 text-[11px] text-slate-400">
                        <Users className="h-3 w-3" /> {s.capacity}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-7 text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-8 text-center"
               data-testid="live-public-no-sessions">
              We are putting together the calendar for the coming weeks. Sign up and we will let you
              know as soon as the classes are published.
            </p>
          )}
        </div>
      </div>

      {/* Registro */}
      <div id="registro" className="bg-slate-50 text-slate-900 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-14 grid md:grid-cols-[1fr_380px] gap-10">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
              Try {dias} days, pay nothing
            </h2>
            <ul className="mt-5 space-y-2.5 text-[14px] text-slate-700">
              {[
                "Book as many live classes as you want during the trial",
                "Verb conjugation trainer",
                "Resource library with the material from every class",
                "You get a 6-digit code by email and you are in right away",
              ].map((t) => (
                <li key={t} className="flex gap-2.5">
                  <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-[12.5px] text-slate-500 mt-5 leading-relaxed">
              When the trial ends we send you the invoice for the month (${Number(precio).toFixed(2)})
              with a payment link. If you decide not to continue, you do nothing: nothing is charged
              automatically and we stop invoicing you.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 h-fit shadow-sm">
            <LivePassSignupCard trialDays={dias} initialEmail={paso.email} initialDone={paso.code} />
          </div>
        </div>
      </div>

      <div className="bg-[#0d1b2a] text-center py-7 text-[12px] text-slate-400">
        We Speak Spanish · Santa Ana, Costa Rica · hola@wespeakspanish-cr.com
      </div>
    </div>
  );
}
